// =============================================================================
// io/ai/aiCoach.ts — the managed Claude proxy.
// -----------------------------------------------------------------------------
// The `ai-coach` Edge Function holds the Anthropic key, the coach persona, and
// the per-user run cap. The client never sees any of the three, and there is no
// path here that could send one — that is the entire reason this is a proxy and
// not a direct call.
//
// ── 🚨 EVERY CALL COSTS MONEY AND BURNS A CAPPED RUN ──
//
// The function enforces a per-user DAILY cap and a per-item cap, and answers
// `COACH_CAP` with a `reason` when either is hit. So a render loop here is not a
// performance bug — it is a bill, and it locks the trader out of their own
// allowance for the rest of the day. The caller owns the cache and the in-flight
// guard; this module refuses to be clever about either.
//
// ── ⚠ THREE ERRORS ARE NORMAL, NOT FAILURES ──
//
//   AI_NOT_CONFIGURED   no Anthropic key is set on the deployment
//   AI_NOT_ENABLED      this user is not entitled to coaching
//   COACH_CAP           allowance spent (reason: 'item_cap' | 'daily_cap')
//
// The old client swallows the first two silently and keeps its templated
// verdict. This client has no template, so it says which of the three happened —
// a card that goes quiet for three different reasons and explains none of them
// is the same failure as a leak row with a bare em dash.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { ENV } from '../../env';

export type AiErrorCode =
  | 'AI_NOT_CONFIGURED' | 'AI_NOT_ENABLED' | 'COACH_CAP' | 'AI_FAILED';

export class AiCoachError extends Error {
  code: AiErrorCode;
  /** `COACH_CAP` only — 'item_cap' | 'daily_cap'. */
  reason: string | null;
  constructor(message: string, code: AiErrorCode, reason: string | null = null) {
    super(message);
    this.name = 'AiCoachError';
    this.code = code;
    this.reason = reason;
  }
}

/**
 * The intel-report verdict, exactly as the function's JSON schema declares it.
 *
 * ⚠ `fixFirst` is DECLARED as three strings and validated as an array here
 * anyway. It is model output — the schema is an instruction, not a guarantee —
 * and a card that assumed three would render `undefined` into the trader's
 * highest-leverage action.
 */
export interface AiVerdict {
  verdict: string;
  fixFirst: string[];
  watch: string;
}

function parseVerdict(raw: unknown): AiVerdict | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const verdict = typeof r['verdict'] === 'string' ? r['verdict'].trim() : '';
  if (!verdict) return null;
  return {
    verdict,
    fixFirst: (Array.isArray(r['fixFirst']) ? r['fixFirst'] : [])
      .filter((x): x is string => typeof x === 'string' && !!x.trim())
      .slice(0, 3),
    watch: typeof r['watch'] === 'string' ? r['watch'].trim() : '',
  };
}

/**
 * Ask the coach for a verdict.
 *
 * @param cacheKey passed through to the function's own cache, so an identical
 *   ask inside its window does not bill twice.
 *
 * ⚠ THE USER'S SESSION TOKEN IS THE AUTHORISATION, and it must be current. A
 * stale one reads as "not signed in" and the card would tell the trader to sign
 * in on a screen they are plainly signed into.
 */
export async function fetchAiVerdict(
  sb: SupabaseClient,
  data: Record<string, unknown>,
  cacheKey: string,
  signal?: AbortSignal,
): Promise<AiVerdict> {
  const { data: sess } = await sb.auth.getSession();
  const token = sess?.session?.access_token;
  if (!token) {
    throw new AiCoachError('Not signed in — sign in for AI coaching.', 'AI_FAILED');
  }

  let res: Response;
  try {
    res = await fetch(`${ENV.config.supabaseUrl}/functions/v1/ai-coach`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: ENV.config.supabaseAnonKey,
      },
      body: JSON.stringify({ scenario: 'intel_report', data, cacheKey }),
      ...(signal ? { signal } : {}),
    });
  } catch (e) {
    throw new AiCoachError(
      `The coach could not be reached: ${e instanceof Error ? e.message : String(e)}`,
      'AI_FAILED',
    );
  }

  const out = await res.json().catch(() => ({})) as Record<string, unknown>;

  if (!res.ok) {
    const code = (typeof out['errorCode'] === 'string' ? out['errorCode'] : 'AI_FAILED') as AiErrorCode;
    throw new AiCoachError(
      typeof out['error'] === 'string' ? out['error'] : `ai-coach returned ${res.status}`,
      code,
      typeof out['reason'] === 'string' ? out['reason'] : null,
    );
  }

  const parsed = parseVerdict(out['result']);
  if (!parsed) {
    // ⚠ NOT treated as an empty verdict. The model returned something that is
    // not the agreed shape, and rendering a blank card would read as "the coach
    // has nothing to say about your trading" — which is a different claim.
    throw new AiCoachError(
      'The coach replied in a shape this screen could not read.', 'AI_FAILED',
    );
  }
  return parsed;
}
